import { ref } from 'vue';
import { physicalLocationApi, type PhysicalLocation } from '@/entities/Document';
import { toastError, toastSuccess, useCustomToast } from '@/shared/lib';

import { extractPhysicalLocationNameErrors } from './extractPhysicalLocationNameErrors';
import { documentManagementMessages } from './messages';

interface CreatePhysicalLocationsSuccessResult {
  success: true;
  createdLocations: PhysicalLocation[];
  nameErrors?: never;
}

interface CreatePhysicalLocationsFailureResult {
  success: false;
  createdLocations?: never;
  nameErrors?: string[];
}

type CreatePhysicalLocationsResult =
  | CreatePhysicalLocationsSuccessResult
  | CreatePhysicalLocationsFailureResult;

function prepareNames(names: string[]): {
  requestNames: string[];
  requestToOriginalIndexes: number[];
} {
  const requestNames: string[] = [];
  const requestToOriginalIndexes: number[] = [];

  for (const [index, name] of names.entries()) {
    const trimmedName = name.trim();

    if (!trimmedName) {
      continue;
    }

    requestNames.push(trimmedName);
    requestToOriginalIndexes.push(index);
  }

  return {
    requestNames,
    requestToOriginalIndexes,
  };
}

export function useCreatePhysicalLocations() {
  const toast = useCustomToast();
  const isCreating = ref(false);

  async function createPhysicalLocations(names: string[]): Promise<CreatePhysicalLocationsResult> {
    isCreating.value = true;
    const { requestNames, requestToOriginalIndexes } = prepareNames(names);

    try {
      const createdLocations = await physicalLocationApi.createPhysicalLocations({
        names: requestNames,
      });

      toastSuccess(toast, documentManagementMessages.physicalLocations.createSuccessToast);

      return {
        success: true,
        createdLocations,
      };
    } catch (error) {
      const nameErrors = extractPhysicalLocationNameErrors(error, requestToOriginalIndexes);

      if (nameErrors) {
        return {
          success: false,
          nameErrors,
        };
      }

      toastError(toast, documentManagementMessages.physicalLocations.createErrorToast);

      return {
        success: false,
      };
    } finally {
      isCreating.value = false;
    }
  }

  return {
    isCreating,
    createPhysicalLocations,
  };
}
